import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { JsonService } from '../_services/json.service';
import { LoadingService } from '../_services/loading.service';
import { LoggerService } from '../_services/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { UserPerformance } from '../_models/user-performance.model';

@Component({
  selector: 'app-user-performance',
  templateUrl: './user-performance.component.html',
  styleUrls: ['./user-performance.component.scss']
})
export class UserPerformanceComponent implements OnInit {
  performanceData: UserPerformance[] = [];
  filteredData: UserPerformance[] = [];
  groupedData: Map<string, UserPerformance[]> = new Map();
  
  currentPage = 1;
  pageSize = 15;
  totalItems = 0;

  listaUsuarios: any[] = [];
  listaPaises: any[] = [];

  filtro: any = {
    usuario: '',
    pais: '',
    dataInicial: '',
    dataFinal: ''
  };

  loading: boolean = false;
  filtroError: string = '';

  @ViewChild('csv_buttons', { static: true }) csv_buttons!: ElementRef<HTMLDivElement>;

  constructor(
    private jsonService: JsonService,
    private loadingService: LoadingService,
    private logger: LoggerService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    this.filtro.dataInicial = today;
    this.filtro.dataFinal = today;
    this.loadCombosList();
    this.loadCountries();
  }

  private loadCombosList(): void {
    this.jsonService.listGroupUsers().subscribe(
      resp => {
        this.listaUsuarios = resp.sort((a: any, b: any) => a.email.localeCompare(b.email));
      },
      erro => {
        this.logger.error('Erro ao carregar usuários', erro);
      }
    );
  }

  private loadCountries(): void {
    this.jsonService.listCountry().subscribe(
      resp => {
        this.listaPaises = resp.sort((a: any, b: any) => a.name.localeCompare(b.name));
      },
      erro => {
        this.logger.error('Erro ao carregar países', erro);
      }
    );
  }

  loadData(): void {
    const hasFilter = this.filtro.usuario || this.filtro.pais || this.filtro.dataInicial || this.filtro.dataFinal;
    if (!hasFilter) {
      this.filtroError = this.translate.instant('Informe ao menos um filtro para realizar a pesquisa.');
      this.loadingService.hide();
      return;
    }
    this.filtroError = '';

    let params = new HttpParams();

    if (this.filtro.usuario) {
      params = params.append('userEmail', this.filtro.usuario);
    }
    if (this.filtro.pais) {
      params = params.append('countryId', this.filtro.pais);
    }
    const n = new Date();
    const todayLocal = `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
    params = params.append('startDate', this.filtro.dataInicial || todayLocal);
    params = params.append('endDate', this.filtro.dataFinal || todayLocal);

    this.jsonService.getUserPerformanceTracking(params).subscribe({
      next: resp => {
        this.performanceData = resp || [];
        this.filteredData = this.performanceData;
        this.totalItems = this.filteredData.length;
        this.currentPage = 1;
        this.loadingService.hide();
      },
      error: err => {
        this.logger.error('Erro ao carregar dados de performance', err);
        this.loadingService.hide();
      }
    });
  }

  private groupByCountry(data: UserPerformance[]): Map<string, UserPerformance[]> {
    const grouped = new Map<string, UserPerformance[]>();
    
    data.forEach(item => {
      const countryName = item.nomePais;
      if (!grouped.has(countryName)) {
        grouped.set(countryName, []);
      }
      grouped.get(countryName)!.push(item);
    });

    // Ordenar países alfabeticamente
    const sortedMap = new Map([...grouped.entries()].sort());
    return sortedMap;
  }

  atualizaFiltro(): void {
    this.loadingService.show();
    this.loadData();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
  }

  arredondar(valor: number): number {
    return Math.ceil(valor);
  }

  exportToExcel(): void {
    if (!this.filteredData || this.filteredData.length === 0) {
      alert(this.translate.instant('Sem dados para exportar'));
      return;
    }

    const csv = this.convertToCSV(this.filteredData);
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const [anoStr, mesStr] = (this.filtro.dataInicial || new Date().toISOString().split('T')[0]).split('-');
    const aaaamm = `${anoStr}${mesStr}`;
    const countryCode = (this.listaPaises.find((p: any) => p.id === this.filtro.pais) as any)?.countryCode || '';
    link.setAttribute('href', url);
    const ts = new Date(); const stamp = `${ts.getFullYear()}${String(ts.getMonth()+1).padStart(2,'0')}${String(ts.getDate()).padStart(2,'0')}${String(ts.getHours()).padStart(2,'0')}${String(ts.getMinutes()).padStart(2,'0')}${String(ts.getSeconds()).padStart(2,'0')}`;
    link.setAttribute('download', `${aaaamm}–${countryCode}–performance-usuario_${stamp}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private convertToCSV(data: UserPerformance[]): string {
    const headers = [
      'País',
      'E-mail do Usuário',
      'Data',
      'Hora Início',
      'Hora Fim',
      'Total Tempo de Trabalho',
      'Tempo Médio',
      'Tempo Médio de Transição',
      'Total de Receitas',
      'Total de Produtos'
    ];

    const rows = data.map(item => [
      item.nomePais,
      item.emailRevisor,
      item.dataRevisao ? new Date(item.dataRevisao).toLocaleDateString() : '',
      item.initialOperation,
      item.finalOperation,
      item.totalTempoPermanencia,
      item.mediaTempoPermanencia,
      item.mediaTempoTransicaoPorReceita,
      item.totalReceitas,
      item.totalPrescricoes
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    return csvContent;
  }

  private formatTime(value: string): string {
    if (!value) return '';
    return value.split('.')[0];
  }

  private formatMinutes(minutes: number): string {
    if (!minutes && minutes !== 0) return '';
    
    const totalSeconds = Math.round(minutes * 60);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const hrsStr = hrs > 0 ? String(hrs).padStart(2, '0') + ':' : '';
    const minsStr = String(mins).padStart(2, '0');
    const secsStr = String(secs).padStart(2, '0');

    return hrs > 0 ? `${hrsStr}${minsStr}:${secsStr}` : `${minsStr}:${secsStr}`;
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }

  clearFiltros(): void {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    this.filtro = {
      usuario: '',
      pais: '',
      dataInicial: today,
      dataFinal: today
    };
    this.performanceData = [];
    this.filteredData = [];
    this.totalItems = 0;
    this.filtroError = '';
  }
}
